import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Layers, FileCheck, CheckCircle2, Trash2, Lock, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

const VenueDetail = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [areas, setAreas] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    fetchData();
  }, [venueId]);

  const fetchData = async () => {
    try {
      const [venueRes, areasRes, submissionsRes, progressRes] = await Promise.all([
        api.get(`/venues/${venueId}`),
        api.get(`/areas/venue/${venueId}`),
        api.get(`/submissions/venue/${venueId}`),
        api.get(`/areas/venue/${venueId}/progress`),
      ]);
      setVenue(venueRes.data);
      setAreas(areasRes.data);
      
      // Deduplicate submissions by submission_id
      const uniqueSubmissions = Array.from(
        new Map(submissionsRes.data.map(sub => [sub.submission_id, sub])).values()
      );
      setSubmissions(uniqueSubmissions);
      setProgress(progressRes.data);
      
      // Check if any submission is locked
      const hasLockedSubmission = uniqueSubmissions.some(s => s.is_locked || s.status === 'locked');
      setIsLocked(hasLockedSubmission);
    } catch (error) {
      toast.error('Failed to load venue data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArea = async () => {
    if (isLocked) {
      toast.error('Cannot add areas - submission is locked');
      return;
    }
    
    const customAreas = areas.filter((a) => a.type === 'custom');
    if (customAreas.length >= 10) {
      toast.error('Maximum 10 areas allowed per venue');
      return;
    }

    navigate(`/venue/${venueId}/area/new`);
  };

  const handleStartSubmission = async () => {
    try {
      const response = await api.post('/submissions', { venue_id: venueId });
      toast.success('Submission started!');
      navigate(`/venue/${venueId}/submission/${response.data.submission_id}/review`);
    } catch (error) {
      toast.error('Failed to start submission');
    }
  };

  const handleSubmitForReview = () => {
    if (activeSubmission) {
      navigate(`/venue/${venueId}/submission/${activeSubmission.submission_id}/review`);
    }
  };

  const handleDeleteArea = async (areaId, areaName) => {
    if (isLocked) {
      toast.error('Cannot delete areas - submission is locked');
      return;
    }
    setItemToDelete({ type: 'area', id: areaId, name: areaName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteVenue = async () => {
    if (isLocked) {
      toast.error('Cannot delete venue - has locked submission');
      return;
    }
    setItemToDelete({ type: 'venue', id: venueId, name: venue?.name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'area') {
        await api.delete(`/areas/${itemToDelete.id}`);
        toast.success(`Area "${itemToDelete.name}" deleted`);
        fetchData(); // Refresh data
      } else if (itemToDelete.type === 'venue') {
        await api.delete(`/venues/${itemToDelete.id}`);
        toast.success(`Venue "${itemToDelete.name}" deleted`);
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : `Failed to delete ${itemToDelete.type}`;
      toast.error(errorMsg);
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const customAreas = areas.filter((a) => a.type === 'custom');
  const systemAreas = areas.filter((a) => a.type === 'system');
  const activeSubmission = submissions.find((s) => s.status === 'draft' || s.status === 'ready');

  return (
    <div className="min-h-screen p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-12">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" data-testid="venue-name">
                {venue?.name}
              </h1>
              <p className="text-muted-foreground">{venue?.address}</p>
            </div>
            {isLocked && (
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" />
                Submission Locked
              </div>
            )}
          </div>
        </div>

        {isLocked && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-emerald-900">Submission Complete</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  Your sensory assessment is complete. You can now generate your accessibility guide instantly.
                </p>
              </div>
            </div>
            {/* View Experience Hub Button - Primary action for completed venues */}
            {submissions.length > 0 && (
              <Button
                onClick={() => navigate(`/guide/${submissions[0].submission_id}`)}
                className="w-full mt-4 h-12 rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="view-experience-hub-button"
              >
                <Eye className="w-5 h-5 mr-2" />
                View Experience Hub
              </Button>
            )}
          </div>
        )}

        {/* Design Brief Card - REMOVED from UI (backend preserved) */}

        {progress && progress.total_areas > 0 && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Overall Progress</h3>
                <p className="text-sm text-blue-700">
                  {progress.total_completed} of {progress.total_required} categories completed
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-900">{progress.overall_progress}%</div>
                <p className="text-xs text-blue-600">Complete</p>
              </div>
            </div>
            <div className="h-3 bg-blue-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${progress.overall_progress}%` }}
              />
            </div>
            {progress.overall_progress === 100 && (
              <div className="mt-4 flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Ready to submit!</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-card rounded-xl border border-border/50 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 rounded-lg p-3">
                <Layers className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Areas</h3>
                <p className="text-sm text-muted-foreground">
                  {customAreas.length}/10 areas created
                </p>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Create areas within your venue such as entrances, toilets, exhibits, seating areas, cafés, activity spaces, or quiet zones.
            </p>

            <div className="space-y-2">
              {customAreas.length > 0 ? (
                customAreas.map((area) => {
                  const areaProgress = progress?.areas?.find(p => p.area_id === area.area_id);
                  const completed = areaProgress?.completed || 0;
                  const isComplete = areaProgress?.is_complete || false;
                  
                  return (
                    <div
                      key={area.area_id}
                      className={`p-3 bg-secondary/50 rounded-lg transition-colors relative group ${isLocked ? 'cursor-default' : 'hover:bg-secondary cursor-pointer'}`}
                      data-testid={`area-${area.area_id}`}
                    >
                      <div 
                        className="flex items-start justify-between"
                        onClick={() => !isLocked && navigate(`/venue/${venueId}/area/${area.area_id}`)}
                      >
                        <div className="flex-1">
                          <p className="font-medium">{area.name}</p>
                          {area.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                              {area.description}
                            </p>
                          )}
                        </div>
                        <div className="ml-3 flex items-center gap-2">
                          {isComplete ? (
                            <div className="flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Complete
                            </div>
                          ) : (
                            <span className={`text-xs font-semibold ${completed > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {completed}/7
                            </span>
                          )}
                          {!isLocked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteArea(area.area_id, area.name);
                              }}
                              data-testid={`delete-area-${area.area_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {!isComplete && completed > 0 && (
                        <div className="mt-2">
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-amber-500 transition-all duration-300"
                              style={{ width: `${(completed / 7) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No areas created yet</p>
              )}
            </div>

            {!isLocked && (
              <Button
                onClick={handleCreateArea}
                className="w-full mt-4 h-11 rounded-full font-medium"
                disabled={customAreas.length >= 10}
                data-testid="create-area-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                {customAreas.length === 0 ? 'Create First Area' : 'Add Another Area'}
              </Button>
            )}
            
            {isLocked && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900 mb-1">
                      Submission Complete
                    </p>
                    <p className="text-xs text-amber-700">
                      This submission is locked. To add more areas, please delete this submission and create a new one with all required areas.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-card rounded-xl border border-border/50 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 rounded-lg p-3">
                <FileCheck className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Submissions</h3>
                <p className="text-sm text-muted-foreground">
                  {submissions.length} total submission{submissions.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {submissions.length > 0 ? (
              <div className="space-y-2">
                {submissions.filter(s => s.status !== 'draft').slice(0, 5).map((sub) => (
                  <div
                    key={sub.submission_id}
                    className="p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors cursor-pointer"
                    onClick={() => navigate(`/submission/${sub.submission_id}/summary`)}
                    data-testid={`submission-${sub.submission_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">ID: {sub.submission_id.slice(0, 12)}...</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          sub.status === 'locked'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {sub.status}
                      </span>
                    </div>
                  </div>
                ))}
                {submissions.filter(s => s.status !== 'draft').length === 0 && (
                  <p className="text-sm text-muted-foreground">No completed submissions yet</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No completed submissions yet</p>
            )}
            
            {customAreas.length >= 1 && progress?.has_draft && !isLocked && (
              <Button
                onClick={handleSubmitForReview}
                className="w-full mt-4 h-11 rounded-full font-medium"
                data-testid="submit-review-button"
              >
                Review & Submit
              </Button>
            )}
            
            {isLocked && submissions.find(s => s.status === 'locked') && (
              <Button
                onClick={() => navigate(`/submission/${submissions.find(s => s.status === 'locked').submission_id}/summary`)}
                className="w-full mt-4 h-11 rounded-full font-medium"
                variant="outline"
                data-testid="view-summary-button"
              >
                <Lock className="w-4 h-4 mr-2" />
                View Assessment Summary
              </Button>
            )}
            
            {/* View Experience Hub - show for any submission (guide can be generated from draft) */}
            {submissions.length > 0 && (
              <Button
                onClick={() => navigate(`/guide/${submissions[0].submission_id}`)}
                className="w-full mt-3 h-11 rounded-full font-semibold"
                variant="default"
                data-testid="view-experience-button"
              >
                <Eye className="w-4 h-4 mr-2" />
                Open Experience Hub
              </Button>
            )}
          </div>
        </div>

        {activeSubmission && !isLocked && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8"
          >
            <h3 className="font-semibold text-blue-900 mb-2">Active Submission</h3>
            <p className="text-sm text-blue-700 mb-4">
              You have a submission in progress. Continue adding areas and completing assessments.
            </p>
            <Button
              onClick={() => navigate(`/venue/${venueId}/area/new`, {
                state: { submission_id: activeSubmission.submission_id },
              })}
              className="rounded-full"
              data-testid="continue-submission-button"
            >
              Continue Submission
            </Button>
          </motion.div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {itemToDelete?.type === 'venue' ? 'Venue' : 'Area'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
              {itemToDelete?.type === 'area' && (
                <span className="block mt-2 text-destructive">
                  This will permanently remove all sensory assessments for this area.
                </span>
              )}
              {itemToDelete?.type === 'venue' && (
                <span className="block mt-2 text-destructive">
                  This will permanently remove all areas, assessments, and submissions for this venue.
                </span>
              )}
              <span className="block mt-2">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VenueDetail;
